let authUrl = import.meta.env.VITE_NEON_AUTH_URL as string | undefined;
let neonAuthClientUrl: string | undefined;

export let neonAuthConfigured = Boolean(authUrl);

type AuthResult = {
  data?: {
    token?: string | null;
    session?: {
      token?: string | null;
    } | null;
  } | null;
  error?: {
    message?: string | null;
  } | null;
};

type SessionResult = {
  data?: {
    session?: {
      token?: string | null;
    } | null;
  } | null;
  error?: {
    message?: string | null;
  } | null;
};

type AuthClient = {
  getSession?: (input?: { query?: { disableCookieCache?: boolean; disableRefresh?: boolean } }) => Promise<SessionResult>;
  requestPasswordReset(input: { email: string; redirectTo: string }): Promise<AuthResult>;
  resetPassword(input: { newPassword: string; token: string }): Promise<AuthResult>;
  signOut?: () => Promise<AuthResult | void>;
  signIn: {
    email(input: { email: string; password: string; callbackURL?: string; rememberMe?: boolean }): Promise<AuthResult>;
    social(input: { provider: "google"; callbackURL?: string; newUserCallbackURL?: string; errorCallbackURL?: string; requestSignUp?: boolean }): Promise<AuthResult>;
  };
};

type NeonAuthClient = {
  adapter: AuthClient;
  getJWTToken: () => Promise<string | null>;
};

let neonAuthClientPromise: Promise<NeonAuthClient | null> | undefined;
let latestAuthToken: string | null = null;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

function setRuntimeAuthUrl(value: string | undefined): void {
  const normalized = value?.trim();
  if (!normalized || normalized === authUrl) {
    neonAuthConfigured = Boolean(authUrl);
    return;
  }

  authUrl = normalized;
  neonAuthConfigured = true;
  if (neonAuthClientUrl && neonAuthClientUrl !== normalized) {
    neonAuthClientPromise = undefined;
    neonAuthClientUrl = undefined;
  }
}

export function configureAuthUrl(value: string | undefined): void {
  setRuntimeAuthUrl(value);
}

async function resolveAuthUrl(): Promise<string | undefined> {
  if (authUrl) {
    return authUrl;
  }

  const response = await fetch("/config", { cache: "no-store" }).catch(() => null);
  if (!response?.ok) {
    neonAuthConfigured = false;
    return undefined;
  }

  const payload = await response.json().catch(() => null) as { neon?: { authUrl?: string } } | null;
  setRuntimeAuthUrl(payload?.neon?.authUrl);
  return authUrl;
}

async function getNeonAuthClient(): Promise<NeonAuthClient | null> {
  const resolvedAuthUrl = await resolveAuthUrl();
  if (!resolvedAuthUrl) {
    return null;
  }

  if (!neonAuthClientPromise || neonAuthClientUrl !== resolvedAuthUrl) {
    neonAuthClientUrl = resolvedAuthUrl;
    neonAuthClientPromise = Promise.all([
      import("@neondatabase/auth"),
      import("@neondatabase/auth/react"),
    ]).then(([{ createInternalNeonAuth }, { BetterAuthReactAdapter }]) =>
      createInternalNeonAuth(resolvedAuthUrl, {
        adapter: BetterAuthReactAdapter(),
      }) as NeonAuthClient,
    );
  }

  return neonAuthClientPromise;
}

export async function getAuthClient(): Promise<AuthClient | null> {
  return (await getNeonAuthClient())?.adapter ?? null;
}

export function getAuthUrl(): string | undefined {
  return authUrl;
}

export async function getAuthToken(): Promise<string | null> {
  const neonAuthClient = await getNeonAuthClient();
  if (!neonAuthClient) {
    return null;
  }

  const session = await neonAuthClient.adapter.getSession?.({
    query: {
      disableCookieCache: true,
    },
  });
  const sessionToken = session?.data?.session?.token;
  if (sessionToken) {
    latestAuthToken = sessionToken;
    return sessionToken;
  }

  if (latestAuthToken) {
    return latestAuthToken;
  }

  return neonAuthClient.getJWTToken().catch(() => null);
}

export function rememberAuthResult(result: AuthResult): void {
  latestAuthToken = result.data?.token ?? result.data?.session?.token ?? latestAuthToken;
}

export async function waitForAuthToken({
  attempts = 8,
  delayMs = 150,
}: {
  attempts?: number;
  delayMs?: number;
} = {}): Promise<string | null> {
  for (let index = 0; index < attempts; index += 1) {
    const token = await getAuthToken().catch(() => null);
    if (token) {
      return token;
    }

    if (index < attempts - 1) {
      await delay(delayMs);
    }
  }

  return null;
}

export async function signOut(): Promise<void> {
  const authClient = await getAuthClient();
  await authClient?.signOut?.();
  latestAuthToken = null;
}
